import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../state/auth';
import { useTranslation } from '../../i18n';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Shield,
  Settings,
  Key,
  MapPin,
  Clock,
  Mail,
  Phone,
  Search,
  Filter,
  MoreVertical,
  UserPlus,
  Route,
  X,
  CreditCard
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { GuardQRGenerator } from '../../features/admin/GuardQRGenerator';
import { fetchGuardDirectory, createGuardRecord } from '../../services/guardDirectory';
import type { GuardRecord } from '../../lib/db';

const qrGeneratorStandaloneUrl = new URL("../../../tools/qr-generator-standalone.html", import.meta.url).href;

type Guard = GuardRecord;

interface Route {
  id: string;
  name: string;
  description: string;
  checkpoints: string[];
  estimatedTime: string;
  difficulty: 'easy' | 'medium' | 'hard';
  status: 'active' | 'inactive';
}

export const AdminPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const formatRole = (role: string) => {
    switch (role.toUpperCase()) {
      case 'ADMIN':
        return t('admin.roleAdmin', 'Admin');
      case 'SUPERVISOR':
        return t('admin.roleSupervisor', 'Supervisor');
      case 'GUARD':
        return t('admin.roleGuard', 'Guard');
      case 'DIRECTOR':
        return t('admin.roleDirector', 'Director');
      default:
        return role;
    }
  };

  const formatStatus = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return t('status.active', 'Active');
      case 'INACTIVE':
        return t('status.inactive', 'Inactive');
      case 'SUSPENDED':
        return t('admin.statusSuspended', 'Suspended');
      case 'DEACTIVATED':
        return t('admin.statusDeactivated', 'Deactivated');
      default:
        return status;
    }
  };

  const formatDifficulty = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return t('admin.difficultyEasy', 'Easy');
      case 'medium':
        return t('admin.difficultyMedium', 'Medium');
      case 'hard':
        return t('admin.difficultyHard', 'Hard');
      default:
        return difficulty;
    }
  };

  // Role-based access control
  if (!user || !['ADMIN', 'DIRECTOR'].includes(user.role)) {
    return <Navigate to="/supervisor" replace />;
  }
  const [activeTab, setActiveTab] = useState<'guards' | 'routes' | 'badges' | 'settings'>('guards');
  const [guards, setGuards] = useState<Guard[]>([]);
  const [guardsLoading, setGuardsLoading] = useState(true);
  const [guardsError, setGuardsError] = useState<string | null>(null);

  const [routes, setRoutes] = useState<Route[]>([
    {
      id: 'route-a',
      name: 'Route A - Building Main',
      description: 'Main building patrol including all floors and common areas',
      checkpoints: ['Main Entrance', 'Lobby', 'Floor 1-5', 'Roof Access', 'Emergency Exits'],
      estimatedTime: '45 minutes',
      difficulty: 'medium',
      status: 'active',
    },
    {
      id: 'route-b',
      name: 'Route B - Perimeter',
      description: 'External perimeter check including parking and grounds',
      checkpoints: ['North Gate', 'Parking Areas', 'South Gate', 'Loading Dock', 'Generator'],
      estimatedTime: '30 minutes',
      difficulty: 'easy',
      status: 'active',
    },
    {
      id: 'route-c',
      name: 'Route C - Security Zones',
      description: 'High-security areas requiring special clearance',
      checkpoints: ['Server Room', 'Executive Floor', 'Research Lab', 'Vault Area'],
      estimatedTime: '60 minutes',
      difficulty: 'hard',
      status: 'active',
    },
  ]);

  const [showAddGuard, setShowAddGuard] = useState(false);
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [savingGuard, setSavingGuard] = useState(false);
  const [addGuardError, setAddGuardError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setGuardsLoading(true);
      setGuardsError(null);
      try {
        const directory = await fetchGuardDirectory();
        if (isMounted) {
          setGuards(directory);
        }
      } catch (error) {
        console.error("Failed to load guard directory", error);
        if (isMounted) {
          setGuardsError(t('admin.errorLoadDirectory', 'Unable to load guard directory right now.'));
        }
      } finally {
        if (isMounted) {
          setGuardsLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const getStatusBadgeClass = (status: string) => {
    const value = status.toUpperCase();
    switch (value) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'DEACTIVATED':
        return 'bg-gray-200 text-gray-600 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-blue-50 text-blue-900 border-blue-200';
      case 'SUPERVISOR':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'GUARD':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'hard':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('admin.title')}</h1>
          <p className="text-gray-600 mt-1">{t('admin.subtitle')}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
        {[
          { id: 'guards', label: t('admin.tabGuards', 'Guard Management'), icon: Users },
          { id: 'routes', label: t('admin.tabRoutes', 'Route Management'), icon: Route },
          { id: 'badges', label: t('admin.tabBadges', 'Badge Generator'), icon: CreditCard },
          { id: 'settings', label: t('admin.tabSettings', 'System Settings'), icon: Settings },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors",
              activeTab === tab.id
                ? "bg-white text-gray-800 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'guards' && (
        <div className="space-y-6">
          {/* Guards Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('admin.searchGuards', 'Search guards...')}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                <Filter className="w-4 h-4" />
                {t('common.filter', 'Filter')}
              </button>
            </div>

            <button
              onClick={() => {
                setAddGuardError(null);
                setShowAddGuard(true);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
            >
              <UserPlus className="w-4 h-4" />
              {t('admin.addGuard', 'Add Guard')}
            </button>
          </div>

          {/* Guards Table */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            {guardsLoading ? (
              <div className="p-10 text-center text-gray-600 text-sm">{t('admin.loadingDirectory', 'Loading guard directory…')}</div>
            ) : guardsError ? (
              <div className="p-6 text-sm text-red-600">{guardsError}</div>
            ) : guards.length === 0 ? (
              <div className="p-10 text-center text-gray-600 text-sm">{t('admin.noGuards', 'No guards found. Add your first guard to get started.')}</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colGuard', 'Guard')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colContact', 'Contact')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colRole', 'Role')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('common.status', 'Status')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colLastActive', 'Last Active')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colRoutes', 'Routes')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('admin.colBadge', 'Badge')}</th>
                    <th className="text-center p-4 text-sm font-medium text-gray-600">{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {guards.map((guard) => (
                    <tr key={guard.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{guard.name}</p>
                            <p className="text-sm text-gray-600">{t('admin.employeeId', 'Employee ID:')} {guard.employeeId}</p>
                            <p className="text-xs text-gray-500">{t('admin.guardId', 'Guard ID:')} {guard.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            {guard.email}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            {guard.phone}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2.5 py-0.5 rounded-md text-xs font-medium border",
                          getRoleBadgeClass(guard.role)
                        )}>
                          {formatRole(guard.role)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2.5 py-0.5 rounded-md text-xs font-medium border",
                          getStatusBadgeClass(guard.status)
                        )}>
                          {formatStatus(guard.status)}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(guard.lastActive))}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {guard.assignedRoutes.map((route) => (
                            <span
                              key={route}
                              className="px-2 py-0.5 text-xs bg-gray-100 rounded-md border border-gray-200"
                            >
                              {route}
                            </span>
                          ))}
                          {guard.assignedRoutes.length === 0 && (
                            <span className="text-xs text-gray-500">{t('admin.noRoutesAssigned', 'No routes assigned')}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {guard.badgeDataUrl ? (
                          <a
                            href={guard.badgeDataUrl}
                            download={`badge-${guard.employeeId || guard.id}.png`}
                            className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-600 hover:bg-gray-50"
                          >
                            {t('admin.viewBadge', 'View badge')}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-500">{t('admin.notGenerated', 'Not generated')}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {guards.some((guard) => guard.status === 'DEACTIVATED') && (() => {
            const deactivated = guards.find((guard) => guard.status === 'DEACTIVATED');
            if (!deactivated) return null;
            return (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-sm font-semibold text-gray-700">{t('admin.deactivatedPreviewTitle', 'Deactivated guard preview')}</h3>
                <p className="mt-1 text-sm text-gray-600">{t('admin.deactivatedPreviewDesc', 'This is how a deactivated account appears to admins.')}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="text-sm text-gray-700">
                    <p className="font-medium">{deactivated.name}</p>
                    <p>{t('admin.employeeId', 'Employee ID:')} {deactivated.employeeId}</p>
                    <p className="text-xs text-gray-500">{t('admin.statusLabel', 'Status:')} {formatStatus(deactivated.status)}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Add Guard Modal */}
          {showAddGuard && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t('admin.addNewGuard', 'Add New Guard')}</h2>
                  <button
                    onClick={() => {
                      setAddGuardError(null);
                      setShowAddGuard(false);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form
                  className="space-y-4"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const name = formData.get("name")?.toString().trim() ?? "";
                    const email = formData.get("email")?.toString().trim() ?? "";
                    const phone = formData.get("phone")?.toString().trim() ?? "";
                    const role = formData.get("role")?.toString().trim() as Guard["role"];
                    const employeeId = formData.get("employeeId")?.toString().trim();
                    const password = formData.get("password")?.toString().trim();
                    const emergencyContact = formData.get("emergencyContact")?.toString().trim() ?? "";
                    if (!name || !email || !phone || !role || !emergencyContact || !password) {
                      setAddGuardError(t('admin.errorAllFields', 'All fields are required.'));
                      return;
                    }
                    setSavingGuard(true);
                    setAddGuardError(null);
                    try {
                      const created = await createGuardRecord({
                        name,
                        email,
                        phone,
                        role,
                        employeeId,
                        emergencyContact,
                        password
                      });
                      setGuards((prev) => [...prev.filter((guard) => guard.id !== created.id), created]);
                      event.currentTarget.reset();
                      setShowAddGuard(false);
                      setAddGuardError(null);
                    } catch (error) {
                      console.error("Unable to add guard", error);
                      setAddGuardError(t('admin.errorAddGuard', 'Unable to add guard. Please try again.'));
                    } finally {
                      setSavingGuard(false);
                    }
                  }}
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldEmployeeId', 'Emory Employee ID')}
                    </label>
                    <input
                      type="text"
                      name="employeeId"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="EMP-1234"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldTempPassword', 'Temporary Password')}
                    </label>
                    <input
                      type="password"
                      name="password"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder={t('admin.placeholderTempPassword', 'Set a temporary password')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldFullName', 'Full Name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldEmail', 'Email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="john.doe@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldPhone', 'Phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldRole', 'Role')}
                    </label>
                    <select
                      name="role"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="GUARD">{t('admin.roleGuard', 'Guard')}</option>
                      <option value="SUPERVISOR">{t('admin.roleSupervisor', 'Supervisor')}</option>
                      <option value="ADMIN">{t('admin.roleAdmin', 'Admin')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldEmergencyContact', 'Emergency Contact')}
                    </label>
                    <input
                      type="text"
                      name="emergencyContact"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Jane Doe (555) 123-4568"
                    />
                  </div>

                  {addGuardError && (
                    <p className="text-sm text-red-600">{addGuardError}</p>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setAddGuardError(null);
                        setShowAddGuard(false);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 rounded-md disabled:opacity-60 transition-opacity hover:opacity-90 brand-green-gradient"
                      disabled={savingGuard}
                    >
                      {savingGuard ? t('common.saving', 'Saving…') : t('admin.addGuard', 'Add Guard')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'routes' && (
        <div className="space-y-6">
          {/* Routes Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">{t('admin.patrolRoutes', 'Patrol Routes')}</h2>
            <button
              onClick={() => setShowAddRoute(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
            >
              <Plus className="w-4 h-4" />
              {t('admin.addRoute', 'Add Route')}
            </button>
          </div>

          {/* Routes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {routes.map((route) => (
              <div key={route.id} className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-1">{route.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{route.description}</p>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-600" />
                          <span className="text-gray-600">{route.estimatedTime}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className={cn("w-4 h-4", getDifficultyColor(route.difficulty))} />
                          <span className={cn(getDifficultyColor(route.difficulty))}>
                            {formatDifficulty(route.difficulty)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <span className={cn(
                      "px-2.5 py-0.5 rounded-md text-xs font-medium border",
                      getStatusBadgeClass(route.status)
                    )}>
                      {formatStatus(route.status)}
                    </span>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('admin.checkpointsCount', 'Checkpoints ({n})').replace('{n}', String(route.checkpoints.length))}</h4>
                    <div className="space-y-1">
                      {route.checkpoints.slice(0, 3).map((checkpoint, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-3 h-3" />
                          {checkpoint}
                        </div>
                      ))}
                      {route.checkpoints.length > 3 && (
                        <p className="text-xs text-gray-500 ml-5">
                          {t('admin.moreCheckpoints', '+{n} more checkpoints').replace('{n}', String(route.checkpoints.length - 3))}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      onClick={() => navigate('/supervisor/schedules')}
                    >
                      <Edit2 className="w-4 h-4 inline mr-1" />
                      {t('common.edit', 'Edit')}
                    </button>
                    <button className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {activeTab === 'badges' && (
        <div className="space-y-6">
          <GuardQRGenerator />
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* System Configuration */}
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {t('admin.systemConfiguration', 'System Configuration')}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.checkpointTimeout', 'Checkpoint Timeout (minutes)')}
                  </label>
                  <input
                    type="number"
                    defaultValue="15"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.gpsThreshold', 'GPS Accuracy Threshold (meters)')}
                  </label>
                  <input
                    type="number"
                    defaultValue="50"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.alertEmail', 'Alert Notification Email')}
                  </label>
                  <input
                    type="email"
                    defaultValue="security@company.com"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <button
                  className="w-full px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
                >
                  {t('admin.saveConfiguration', 'Save Configuration')}
                </button>
              </div>
            </div>

            {/* Security Settings */}
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  {t('admin.securitySettings', 'Security Settings')}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{t('admin.twoFactor', 'Two-Factor Authentication')}</p>
                    <p className="text-sm text-gray-600">{t('admin.twoFactorDesc', 'Require 2FA for all supervisors')}</p>
                  </div>
                  <div className="w-12 h-6 bg-green-500 rounded-md relative">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-sm"></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{t('admin.sessionTimeout', 'Session Timeout')}</p>
                    <p className="text-sm text-gray-600">{t('admin.sessionTimeoutDesc', 'Auto-logout after inactivity')}</p>
                  </div>
                  <select className="border border-gray-300 rounded-md px-3 py-1 text-sm">
                    <option>{t('admin.timeout30Min', '30 minutes')}</option>
                    <option>{t('admin.timeout1Hour', '1 hour')}</option>
                    <option>{t('admin.timeout2Hours', '2 hours')}</option>
                    <option>{t('admin.timeout4Hours', '4 hours')}</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{t('admin.passwordPolicy', 'Password Policy')}</p>
                    <p className="text-sm text-gray-600">{t('admin.passwordPolicyDesc', 'Enforce strong passwords')}</p>
                  </div>
                  <div className="w-12 h-6 bg-green-500 rounded-md relative">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-sm"></div>
                  </div>
                </div>

                <button className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md transition-colors">
                  {t('admin.resetSessions', 'Reset All Sessions')}
                </button>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">{t('admin.systemStatus', 'System Status')}</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">99.9%</div>
                  <p className="text-sm text-gray-600">{t('admin.uptime', 'Uptime')}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">4.2GB</div>
                  <p className="text-sm text-gray-600">{t('admin.storageUsed', 'Storage Used')}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-700 mb-1">1,247</div>
                  <p className="text-sm text-gray-600">{t('admin.totalScans', 'Total Scans')}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600 mb-1">12ms</div>
                  <p className="text-sm text-gray-600">{t('admin.avgResponse', 'Avg Response')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
